import { formatNumberWithSpaces } from "@/composables/formatUtils";

/** Whole MWh with a space thousands separator: 12400 → "12 400". */
export const fmt = (n: number): string => formatNumberWithSpaces(Math.round(n));

/** Share of a total as a CSS percentage string with one decimal. */
export const pctOf = (value: number, total: number, decimals = 1): string =>
  total > 0 ? `${((value / total) * 100).toFixed(decimals)}%` : "0%";

/** "+4 200" / "−3 000" (typographic minus). */
export const signed = (n: number): string =>
  `${n >= 0 ? "+" : "−"}${fmt(Math.abs(n))}`;

export const sum = (values: readonly number[]): number =>
  values.reduce((total, value) => total + value, 0);

/**
 * Strictly parse a hand-typed MWh figure. Accepts digits with optional spaces as thousands
 * separators and one decimal part ("," or "."); empty means 0. Anything else, including a minus
 * sign, is invalid and returns null so the form can show an error instead of saving garbage.
 */
export const parseVolume = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === "") return 0;
  if (!/^\d[\d\s]*([.,]\d+)?$/.test(trimmed)) return null;
  const value = Number(trimmed.replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(value) ? value : null;
};

/** Parse a hand-typed figure ("12 400", "12400", "") to a number, 0 when empty/invalid. */
export const num = (value: string | number | null | undefined): number =>
  Number(
    String(value ?? "")
      .replace(",", ".")
      .replace(/[^\d.]/g, "")
  ) || 0;
